import { CSSProperties, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { useQueryClient } from 'react-query'
import styled from 'styled-components'

import {
  addPointToGroup,
  deletePoint,
  updatePoint,
  updatePointActiveness,
} from '../../../../api/adminBuildingFetches'
import { ProjectFloorSection } from '../../../../api/types'
import iconDirection from '../../../../assets/images/icon_direction.svg'
import iconTrash from '../../../../assets/images/icon_trash.svg'
import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { IMapPoint } from '../../../views/admin_building_page/components/ManagePoints/ManagedMapPoint'
import { useManagePointsContext } from '../../../views/admin_building_page/components/ManagePoints/ManagePointsContext'
import { floorGroupsAndMappingsQueryKey } from '../../../views/admin_building_page/hooks/adminBuildingQueries'
import { MapPointMainCircle } from '../../MapViewer'

import { IGroup, IMapping } from './PointGroupSelector/PointGroupSelector'
import { SelectItem } from './SelectItem'

export const PointControls = () => {
  const [addToGroupExpanded, setAddToGroupExpanded] = useState<boolean>(false)
  const [addToSectionExpanded, setAddToSectionExpanded] =
    useState<boolean>(false)
  const [isAddingToGroup, setIsAddingToGroup] = useState<boolean>(false)
  const isAddingToGroupRef = useRef(false)

  const queryClient = useQueryClient()
  const { updateFloor, state: buildingState } = useBuildingContext()

  const {
    canEditPoints,
    canDeletePoints,
    canCreatePointMappings,
    aimingPointId,
    setAimingPointId,
  } = useManagePointsContext()

  const { addNotification } = useNotifications()

  const clearSelectedPoints = () => updateFloor({ selectedPoints: new Set() })
  const noPointsSelected = !buildingState.floorData.selectedPoints.size
  const isAiming = aimingPointId !== null
  const singlePointSelected = buildingState.floorData.selectedPoints.size === 1

  const selectedPointData = singlePointSelected
    ? buildingState.floorData.points.find(
        (p: IMapPoint) =>
          p.point_id ===
          (Array.from(buildingState.floorData.selectedPoints)[0] as number),
      )
    : null

  const selectedPointsHaveImages =
    Array.isArray(buildingState.floorData.images) &&
    buildingState.floorData.images.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
      (image: any) => {
        return buildingState.floorData.selectedPoints.has(image.sub_point_id)
      },
    )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper return type
  const mapSelectedPoints = (callback: (pointId: number) => Promise<any>) => {
    const selectedPointsArr: number[] = Array.from(
      buildingState.floorData.selectedPoints,
    )

    const promiseArr = selectedPointsArr.map(pointId => {
      return callback(pointId)
    })

    Promise.all(promiseArr).then((returnedPoints: IMapPoint[]) => {
      const returnedPointsSet = new Set(
        returnedPoints.map(point => point.point_id),
      )
      let points = [...buildingState.floorData.points]

      points = points.filter(point => !returnedPointsSet.has(point.point_id))

      points = [...points, ...returnedPoints]

      updateFloor({
        points,
      })
    })
  }

  const activateSelected = () => {
    mapSelectedPoints((pointId: number) =>
      updatePointActiveness(
        buildingState.projectId,
        buildingState.floorId,
        pointId.toString(),
        true,
      ),
    )
  }

  const deactivateSelected = () => {
    mapSelectedPoints((pointId: number) =>
      updatePointActiveness(
        buildingState.projectId,
        buildingState.floorId,
        pointId.toString(),
        false,
      ),
    )
  }

  const clearYawSelected = () => {
    mapSelectedPoints((pointId: number) =>
      updatePoint(
        buildingState.projectId,
        buildingState.floorId,
        pointId.toString(),
        { yaw: null },
      ),
    )
  }

  const pointIsAlreadyInGroup = (pointId: number, targetGroupId: number) => {
    const pointGroups = buildingState.floorData.pointMappings.get(pointId)
    return pointGroups?.has(targetGroupId) ?? false
  }

  const addSelectedToGroup = (groupId: number) => {
    if (isAddingToGroupRef.current) {
      return
    }

    const selectedPointsArr: number[] = Array.from(
      buildingState.floorData.selectedPoints,
    )

    const pointsToAdd = selectedPointsArr.filter(
      pointId => !pointIsAlreadyInGroup(pointId, groupId),
    )

    if (pointsToAdd.length === 0) {
      setAddToGroupExpanded(false)
      addNotification('Selected points are already in this group', 'success')
      return
    }

    isAddingToGroupRef.current = true
    setIsAddingToGroup(true)

    const promiseArr = pointsToAdd.map(pointId => {
      return addPointToGroup(
        buildingState.projectId,
        buildingState.floorId,
        pointId.toString(),
        groupId.toString(),
      )
    })

    Promise.allSettled(promiseArr)
      .then(results => {
        const pointMappings = new Map<number, Set<number>>(
          buildingState.floorData.pointMappings,
        )
        let hadError = false

        const addGroupToPointMapping = (
          pointId: number,
          mappedGroupId: number,
        ) => {
          const pointGroups: Set<number> = pointMappings.has(pointId)
            ? pointMappings.get(pointId)!
            : new Set()
          pointGroups.add(mappedGroupId)
          pointMappings.set(pointId, pointGroups)
        }

        results.forEach((result, index) => {
          const pointId = pointsToAdd[index]

          if (result.status === 'fulfilled') {
            const mapping: IMapping | undefined = result.value
            if (mapping?.point_id != null && mapping?.group_id != null) {
              addGroupToPointMapping(
                Number(mapping.point_id),
                Number(mapping.group_id),
              )
            } else {
              addGroupToPointMapping(pointId, groupId)
            }
            return
          }

          const responseStatusCode = result.reason?.response?.status

          if (responseStatusCode === 409) {
            addGroupToPointMapping(pointId, groupId)
            return
          }

          hadError = true
        })

        updateFloor({
          pointMappings,
        })

        setAddToGroupExpanded(false)

        void queryClient.invalidateQueries(
          floorGroupsAndMappingsQueryKey(
            buildingState.projectId,
            buildingState.floorId,
          ),
        )

        if (hadError) {
          addNotification('Error adding point to group', 'error')
        } else {
          addNotification('Points added to group successfully', 'success')
        }
      })
      .catch(() => {
        addNotification('Error adding points to group', 'error')
      })
      .finally(() => {
        isAddingToGroupRef.current = false
        setIsAddingToGroup(false)
      })
  }

  const addSelectedToSection = async (sectionId: number) => {
    const selectedPointsArr: number[] = Array.from(
      buildingState.floorData.selectedPoints,
    )

    const promiseArr = selectedPointsArr.map(pointId => {
      return updatePoint(
        buildingState.projectId,
        buildingState.floorId,
        pointId.toString(),
        {
          project_floor_section: sectionId,
        },
      )
    })

    try {
      await Promise.all(promiseArr)

      addNotification('Points added to section successfully', 'success')
      updateFloor({
        selectedPoints: new Set(),
      })
      setAddToSectionExpanded(false)
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('addSelectedToSection==>>', err)
      addNotification('Error adding points to section', 'error')
    }
  }

  const deleteSelected = () => {
    const selectedPointsArr: number[] = Array.from(
      buildingState.floorData.selectedPoints,
    )
    const deletedPointIds: Set<number> = new Set()

    const promiseArr = selectedPointsArr.map(async pointId => {
      try {
        const deletedPoint: IMapPoint = await deletePoint(
          buildingState.projectId,
          buildingState.floorId,
          pointId.toString(),
        )

        deletedPointIds.add(pointId)

        return deletedPoint
      } catch (err) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.log('deletePoint==>>', pointId)
      }
    })

    Promise.allSettled(promiseArr).then(() => {
      if (deletedPointIds.size > 0) {
        updateFloor({
          points: buildingState.floorData.points.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper point type
            (point: any) => !deletedPointIds.has(point.point_id),
          ),
        })

        const pointsWording: string =
          deletedPointIds.size !== 1 ? 'Points' : 'Point'
        addNotification(
          `${deletedPointIds.size} ${pointsWording} Deleted Successfully.`,
          'success',
        )
      }

      if (deletedPointIds.size < selectedPointsArr.length) {
        const numPointsErrored: number =
          selectedPointsArr.length - deletedPointIds.size
        const pointsWording: string =
          numPointsErrored !== 1 ? 'Points' : 'Point'

        addNotification(
          `Error Deleting ${numPointsErrored} ${pointsWording}.`,
          'error',
        )
      }

      clearSelectedPoints()
    })
  }

  useEffect(() => {
    if (noPointsSelected) {
      setAddToGroupExpanded(false)
      setAddToSectionExpanded(false)
    }
  }, [noPointsSelected])

  const toggleAddToGroup = () => {
    setAddToGroupExpanded(prev => {
      const next = !prev
      if (next) {
        setAddToSectionExpanded(false)
      }
      return next
    })
  }

  const toggleAddToSection = () => {
    setAddToSectionExpanded(prev => {
      const next = !prev
      if (next) {
        setAddToGroupExpanded(false)
      }
      return next
    })
  }

  const renderDropdownItems = (
    items: { id: number; name: string }[],
    onSelect: (id: number) => void,
    disabled?: boolean,
  ) =>
    items.map(item => (
      <SelectItem
        content={item.name}
        key={item.id}
        style={{
          color: disabled ? '#7e858e' : '#212121',
          padding: '8px 12px',
          margin: 0,
          pointerEvents: disabled ? 'none' : undefined,
        }}
        onClick={() => {
          if (!disabled) {
            onSelect(item.id)
          }
        }}
        showMarker={true}
      />
    ))

  return (
    <PointControlContainer>
      {selectedPointData && (
        <PointInfoBox>
          <PointInfoRow>
            <PointInfoLabel>id:</PointInfoLabel>
            <PointInfoValue>{selectedPointData.point_id}</PointInfoValue>
          </PointInfoRow>
          <PointInfoRow>
            <PointInfoLabel>x:</PointInfoLabel>
            <PointInfoValue>{selectedPointData.x}</PointInfoValue>
          </PointInfoRow>
          <PointInfoRow>
            <PointInfoLabel>y:</PointInfoLabel>
            <PointInfoValue>{selectedPointData.y}</PointInfoValue>
          </PointInfoRow>
          <PointInfoRow>
            <PointInfoLabel>yaw:</PointInfoLabel>
            <PointInfoValue>
              {selectedPointData.yaw != null
                ? `${selectedPointData.yaw}°`
                : '—'}
            </PointInfoValue>
          </PointInfoRow>
        </PointInfoBox>
      )}
      {canEditPoints && (
        <>
          <PointControlButton
            onClick={() => {
              if (!noPointsSelected) {
                activateSelected()
              }
            }}
            disabled={noPointsSelected}
          >
            <MapPointMainCircle
              pointSize={12}
              style={mapCircleIconStyle}
              className={classNames({
                back_pink: true,
              })}
            />
            <PointControlSpan>Activate Selected</PointControlSpan>
          </PointControlButton>

          <PointControlButton
            onClick={() => {
              if (!noPointsSelected) {
                deactivateSelected()
              }
            }}
            disabled={noPointsSelected}
          >
            <MapPointMainCircle
              pointSize={12}
              style={mapCircleIconStyle}
              className={classNames({
                back_gray: true,
              })}
            />
            <PointControlSpan>Deactivate Selected</PointControlSpan>
          </PointControlButton>

          <PointControlButton
            onClick={() => {
              if (isAiming) {
                setAimingPointId(null)
              } else if (singlePointSelected) {
                const selectedId = Array.from(
                  buildingState.floorData.selectedPoints,
                )[0] as number
                setAimingPointId(selectedId)
              }
            }}
            disabled={!isAiming && !singlePointSelected}
            style={
              isAiming
                ? { borderColor: '#073C7A', background: '#f0f5ff' }
                : undefined
            }
          >
            <YawIconButton src={iconDirection} alt="" />
            <PointControlSpan>
              {isAiming ? 'Cancel Aim' : 'Aim Yaw'}
            </PointControlSpan>
          </PointControlButton>

          <PointControlButton
            onClick={() => {
              if (!noPointsSelected) {
                clearYawSelected()
              }
            }}
            disabled={noPointsSelected}
          >
            <YawIconButton
              src={iconDirection}
              alt=""
              style={{ opacity: 0.35 }}
            />
            <PointControlSpan>Clear Yaw</PointControlSpan>
          </PointControlButton>
        </>
      )}

      {canCreatePointMappings && (
        <DropdownWrapper>
          <PointControlButton
            onClick={() => {
              if (!noPointsSelected) {
                toggleAddToGroup()
              }
            }}
            disabled={noPointsSelected}
            $expanded={addToGroupExpanded}
          >
            <MapPointMainCircle
              pointSize={12}
              style={mapCircleIconStyle}
              className={classNames({
                back_blue: true,
              })}
            />
            <PointControlSpan>Add to Group</PointControlSpan>
            <DropdownChevron $expanded={addToGroupExpanded} aria-hidden>
              ›
            </DropdownChevron>
          </PointControlButton>

          <DropdownPanel
            aria-expanded={addToGroupExpanded}
            onClick={e => e.stopPropagation()}
          >
            <GroupSelectorWrapper>
              {renderDropdownItems(
                buildingState.floorData.groups.map((group: IGroup) => ({
                  id: group.group_id,
                  name: group.name,
                })),
                addSelectedToGroup,
                isAddingToGroup,
              )}
            </GroupSelectorWrapper>
          </DropdownPanel>
        </DropdownWrapper>
      )}

      {canCreatePointMappings && (
        <DropdownWrapper>
          <PointControlButton
            onClick={() => {
              if (!noPointsSelected) {
                toggleAddToSection()
              }
            }}
            disabled={noPointsSelected}
            $expanded={addToSectionExpanded}
          >
            <span
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                margin: '0 18px 0 14px',
              }}
            >
              &#9633;
            </span>
            <PointControlSpan>Add to Section</PointControlSpan>
            <DropdownChevron $expanded={addToSectionExpanded} aria-hidden>
              ›
            </DropdownChevron>
          </PointControlButton>

          <DropdownPanel
            aria-expanded={addToSectionExpanded}
            onClick={e => e.stopPropagation()}
          >
            <GroupSelectorWrapper>
              {renderDropdownItems(
                buildingState.floorData.sections.map(
                  (section: ProjectFloorSection) => ({
                    id: section.id,
                    name: section.name,
                  }),
                ),
                addSelectedToSection,
              )}
            </GroupSelectorWrapper>
          </DropdownPanel>
        </DropdownWrapper>
      )}

      {canDeletePoints && (
        <PointControlButton
          disabled={noPointsSelected || selectedPointsHaveImages}
          onClick={() => {
            if (!noPointsSelected && !selectedPointsHaveImages) {
              deleteSelected()
            }
          }}
        >
          <TrashIconButton
            src={iconTrash}
            disabled={noPointsSelected || selectedPointsHaveImages}
          />
          <PointControlSpan>Delete Selected</PointControlSpan>
        </PointControlButton>
      )}
    </PointControlContainer>
  )
}

export const PointControlContainer = styled.div`
  padding: 16px 16px 8px;
`

export const PointControlButton = styled.div<{
  disabled?: boolean
  $expanded?: boolean
}>`
  border: 1px solid #e2e8f0;
  border-radius: 2px;
  padding: 8px;
  margin: 8px 0px;
  height: 36px;
  display: flex;
  align-items: center;
  color: ${props => (props.disabled ? '#7e858e' : '#073C7A')};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  background: ${props =>
    props.$expanded && !props.disabled ? '#f0f5ff' : '#ffffff'};
  border-color: ${props =>
    props.$expanded && !props.disabled ? '#073C7A' : '#e2e8f0'};
  box-sizing: border-box;
`

export const PointControlSpan = styled.span`
  display: inline-block;
  font-size: 14px;
`

export const TrashIconButton = styled.img<{ disabled?: boolean }>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  vertical-align: middle;
  margin: 0 14px 0 9px;
`

const YawIconButton = styled.img`
  width: 18px;
  height: 18px;
  margin: 0 14px 0 9px;
  vertical-align: middle;
`

const PointInfoBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 2px;
  padding: 8px 12px;
  margin-bottom: 4px;
  font-size: 13px;
  font-family: monospace;
  color: #334155;
`

const PointInfoRow = styled.div`
  display: flex;
  gap: 6px;
  line-height: 1.6;
`

const PointInfoLabel = styled.span`
  color: #64748b;
  min-width: 30px;
`

const PointInfoValue = styled.span`
  color: #1e293b;
`

const GroupSelectorWrapper = styled.div`
  & > div {
    &:last-child {
      margin-bottom: 0px;
    }

    &:not(&:last-child) {
      border-bottom: 1px solid #e2e8f0;
    }
  }
`

const DropdownWrapper = styled.div`
  margin: 8px 0;

  ${PointControlButton} {
    margin: 0;
  }
`

const DropdownPanel = styled.div`
  display: none;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  max-height: 180px;
  overflow-y: auto;
  padding: 4px 0;

  &[aria-expanded='true'] {
    display: block;
  }
`

const DropdownChevron = styled.span<{ $expanded: boolean }>`
  margin-left: auto;
  margin-right: 4px;
  font-size: 18px;
  line-height: 1;
  transform: rotate(${props => (props.$expanded ? '-90deg' : '90deg')});
  transition: transform 0.2s ease;
`

export const mapCircleIconStyle: CSSProperties = {
  display: 'inline-block',
  position: 'static',
  verticalAlign: 'middle',
  width: '12px',
  height: '12px',
  transform: 'none',
  margin: '0 18px 0 12px',
}
